import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { AutoModeRounded } from '@mui/icons-material';
import { useTenantStore } from '../../../stores/tenant.store';
import { surveyApi, type SurveyTemplateDto } from '../../../lib/api/survey.api';

interface Props {
  sectionId: string;
  templates: SurveyTemplateDto[];
}

/** Per-section opt-in: auto-launch the end-of-course survey when the section
 *  concludes. Toggleable — off by default. */
export function SurveyAutomationCard({ sectionId, templates }: Props) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const queryClient = useQueryClient();

  const [enabled, setEnabled] = useState(false);
  const [templateId, setTemplateId] = useState('');

  // Prefer end-of-course templates for this control.
  const eocTemplates = templates.filter(
    (tpl) => tpl.category === 'end_of_course' || tpl.category === 'generic',
  );

  const configQ = useQuery({
    queryKey: ['survey-automation', currentTenantId, sectionId],
    queryFn: () => surveyApi.getAutomation({ tenantId: currentTenantId!, sectionId }),
    enabled: !!currentTenantId && !!sectionId,
  });

  useEffect(() => {
    const c = configQ.data?.config;
    setEnabled(c?.enabled ?? false);
    setTemplateId(c?.templateId ?? eocTemplates[0]?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configQ.data]);

  const save = useMutation({
    mutationFn: (next: { enabled: boolean; templateId?: string }) =>
      surveyApi.setAutomation({
        tenantId: currentTenantId!,
        sectionId,
        enabled: next.enabled,
        ...(next.templateId ? { templateId: next.templateId } : {}),
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['survey-automation', currentTenantId, sectionId],
      }),
  });

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    save.mutate(checked ? { enabled: true, templateId } : { enabled: false });
  };

  const handleTemplate = (id: string) => {
    setTemplateId(id);
    if (enabled) save.mutate({ enabled: true, templateId: id });
  };

  const config = configQ.data?.config;

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, maxWidth: 760, mt: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
          <AutoModeRounded color="primary" />
          <Typography variant="h6" fontWeight={700}>
            {t('surveys.automation.title')}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('surveys.automation.subtitle')}
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => handleToggle(e.target.checked)}
              disabled={save.isPending || configQ.isLoading}
            />
          }
          label={t('surveys.automation.toggle')}
        />

        {enabled && (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="automation-template-label">
              {t('surveys.automation.template')}
            </InputLabel>
            <Select
              labelId="automation-template-label"
              label={t('surveys.automation.template')}
              value={eocTemplates.some((tpl) => tpl.id === templateId) ? templateId : ''}
              onChange={(e) => handleTemplate(e.target.value)}
            >
              {eocTemplates.map((tpl) => (
                <MenuItem key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {config?.autoLaunched && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {t('surveys.automation.alreadyLaunched')}
          </Alert>
        )}
        {save.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t('surveys.automation.saveError')}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
